import { Coffee, MapPin } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { EmptyState } from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import type { OrderTypeRow } from "@/types";

interface OrderTypeCardProps {
  rows: OrderTypeRow[];
}

const LABELS: Record<OrderTypeRow["orderType"], string> = {
  dine_in: "Dine in",
  takeaway: "Takeaway",
};

const ICONS: Record<OrderTypeRow["orderType"], React.ElementType> = {
  dine_in: Coffee,
  takeaway: MapPin,
};

export function OrderTypeCard({ rows }: OrderTypeCardProps) {
  const totalCount = rows.reduce((sum, r) => sum + r.count, 0);
  const totalRevenue = rows.reduce((sum, r) => sum + r.revenue, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service type</CardTitle>
        <CardDescription>Dine-in vs takeaway</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState
            icon={Coffee}
            title="No orders yet"
            description="Service split shows here once orders come in"
          />
        ) : (
          <ul className="space-y-3">
            {rows.map((row) => {
              const Icon = ICONS[row.orderType];
              const countShare =
                totalCount > 0 ? row.count / totalCount : 0;
              const revenueShare =
                totalRevenue > 0 ? row.revenue / totalRevenue : 0;

              return (
                <li
                  key={row.orderType}
                  className="space-y-1 rounded-md border p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-medium">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      {LABELS[row.orderType]}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {row.count} orders · {formatCurrency(row.revenue)}
                    </span>
                  </div>
                  <div className="flex gap-2 text-[10px] text-muted-foreground">
                    <span>{(countShare * 100).toFixed(0)}% of orders</span>
                    <span>·</span>
                    <span>{(revenueShare * 100).toFixed(0)}% of revenue</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
